import { PexIcon } from '../../components/icons/PexIcon'
import { MobileOperationsList } from '../../components/operations/mobile-operations-list'
import { formatDate } from '../../lib/datetime'
import { formatOrderRef, formatWorkOrderRef } from './workOrderDisplay'
import type {
  WorkOrder,
  WorkOrderFulfillmentMode,
  WorkOrderStatus,
} from '../../mocks/data/work-orders'

export type SupplyMobileWorkOrderListLabels = {
  loading: string
  loadFailed: string
  retry: string
  empty: string
  openWorkOrder: (id: string) => string
  quantity: (count: number) => string
  due: (value: string) => string
  overdue: (days: number) => string
  dueToday: string
  route: Record<WorkOrderFulfillmentMode, string>
  status: Record<WorkOrderStatus, string>
}

type SupplyMobileWorkOrderListProps = {
  workOrders: WorkOrder[]
  labels: SupplyMobileWorkOrderListLabels
  loading?: boolean
  error?: boolean
  now: number
  onOpen: (id: string) => void
  onRetry: () => void
}

const DAY_MS = 24 * 60 * 60 * 1000

const STATUS_TONE: Record<WorkOrderStatus, string> = {
  assigned:
    'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]',
  acknowledged:
    'bg-[color-mix(in_srgb,var(--color-info)_10%,transparent)] text-[var(--color-info)]',
  in_production:
    'bg-[color-mix(in_srgb,var(--color-warning)_12%,transparent)] text-[var(--color-warning)]',
  dispatched_to_warehouse:
    'bg-[color-mix(in_srgb,var(--color-accent)_10%,transparent)] text-[var(--color-accent)]',
  received_at_warehouse:
    'bg-[color-mix(in_srgb,var(--color-success)_10%,transparent)] text-[var(--color-success)]',
  dispatched_to_customer:
    'bg-[color-mix(in_srgb,var(--color-success)_10%,transparent)] text-[var(--color-success)]',
  cancelled:
    'bg-[color-mix(in_srgb,var(--color-danger)_9%,transparent)] text-[var(--color-danger)]',
}

function dueLabel(
  workOrder: WorkOrder,
  now: number,
  labels: SupplyMobileWorkOrderListLabels,
): { text: string; overdue: boolean } {
  const due = new Date(workOrder.due_date).getTime()
  const days = Math.ceil((now - due) / DAY_MS)

  if (days > 0) {
    return { text: labels.overdue(days), overdue: true }
  }
  if (days === 0) {
    return { text: labels.dueToday, overdue: false }
  }
  return {
    text: labels.due(formatDate(workOrder.due_date)),
    overdue: false,
  }
}

export function SupplyMobileWorkOrderList({
  workOrders,
  labels,
  loading = false,
  error = false,
  now,
  onOpen,
  onRetry,
}: SupplyMobileWorkOrderListProps) {
  return (
    <MobileOperationsList
      items={workOrders}
      loading={loading}
      error={error}
      getKey={(workOrder) => workOrder.id}
      labels={{
        loading: labels.loading,
        error: labels.loadFailed,
        retry: labels.retry,
        empty: labels.empty,
      }}
      onRetry={onRetry}
      renderItem={(workOrder) => {
        const deadline = dueLabel(workOrder, now, labels)

        return (
          <article className="px-4 py-3 transition-colors hover:bg-[var(--color-surface-muted)]">
            <button
              type="button"
              className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 text-left outline-none focus-visible:rounded-md focus-visible:shadow-[var(--shadow-focus)]"
              aria-label={labels.openWorkOrder(formatWorkOrderRef(workOrder.id))}
              onClick={() => onOpen(workOrder.id)}
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] bg-[var(--color-text)] text-[var(--color-surface)]">
                  <PexIcon
                    name="factory"
                    size="sm"
                    tone="on-emphasis"
                    decorative
                  />
                </span>
                <span className="truncate font-mono text-[13px] font-semibold tracking-[-0.01em]">
                  {formatWorkOrderRef(workOrder.id)}
                </span>
              </span>
              <span className="whitespace-nowrap text-[15px] font-semibold tabular-nums">
                {labels.quantity(workOrder.quantity)}
              </span>

              <span className="min-w-0 truncate text-[15px] font-semibold tracking-[-0.01em]">
                {workOrder.product_name}
              </span>
              <span
                className={
                  deadline.overdue
                    ? 'whitespace-nowrap text-xs font-semibold text-[var(--color-danger)]'
                    : 'whitespace-nowrap text-xs font-medium text-[var(--color-text-muted)]'
                }
              >
                {deadline.text}
              </span>

              <span className="min-w-0 truncate text-xs text-[var(--color-text-muted)]">
                {workOrder.product_id}
              </span>
              <span className="whitespace-nowrap font-mono text-xs text-[var(--color-text-muted)]">
                {formatOrderRef(workOrder.customer_order_ref)}
              </span>

              <span className="col-span-2 mt-1 flex min-w-0 flex-wrap items-center gap-1.5">
                <span
                  className={`max-w-full truncate rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[workOrder.state]}`}
                >
                  {labels.status[workOrder.state]}
                </span>
                <span className="max-w-full truncate rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[11px] text-[var(--color-text)]">
                  {labels.route[workOrder.fulfillment_mode]}
                </span>
              </span>
            </button>
          </article>
        )
      }}
    />
  )
}
